// Which placement chromes are offered for each canvas shape, what the
// default is, and how the operator's choice persists.
//
// The option list is a function of BOTH the platform family and the
// canvas shape, not the shape alone. A 9:16 creative is Instagram Reels
// when it's a Meta buy and YouTube Shorts when it's a Performance Max
// buy — same pixels, different furniture — so offering Reels on a PMax
// ad previews a placement that ad will never run in. Family comes from
// the ad's own Ad.platformFormat.
//
// Defaults follow the request: chrome is ON, and each shape lands on the
// most common placement for its family (Meta 9:16 → Reels, Google 9:16 →
// Shorts). When the ad declares a specific placement (Ad.platformFormat,
// set by the generation wizard) that wins over the shape default, because
// it's the ad's stated intent rather than our guess. An explicit operator
// selection outranks both and is remembered.

import { familyForPlatformFormat } from './types';
import type { AdRatio, ChromeFamily, ChromeKind, ChromeOption, ChromeScope } from './types';

export const CHROME_LABEL: Record<ChromeKind, string> = {
  'none':        'No chrome',
  // Meta
  'reels':       'Instagram Reels',
  'stories':     'Instagram Stories',
  'ig-feed':     'Instagram Feed',
  'fb-feed':     'Facebook Feed',
  // Google
  'yt-shorts':   'YouTube Shorts',
  'yt-instream': 'YouTube in-stream',
  'yt-infeed':   'YouTube in-feed',
  'discover':    'Google Discover',
  'gdn-display': 'Google Display',
  'gmail':       'Gmail Promotions'
};

// Options per family × canvas shape, most-common first. 'none' sits last
// so the selector reads as "pick a placement", not "pick whether to
// preview".
//
// Meta 9:16 deliberately offers no Feed option: Facebook and Instagram
// Feed both crop a vertical asset to 4:5, so a feed card showing the full
// 9:16 frame would preview something that never ships.
//
// Google's lists are LONGER than Meta's on purpose. A Meta buy targets a
// placement; a Performance Max buy hands one asset to Google and Google
// decides — so every surface below is somewhere the creative may
// genuinely appear, and the operator has no way to opt out of any of
// them. Ordering follows where each recommended asset size actually gets
// the most delivery (services/platformFormats.js pmax_* keys): vertical
// video to Shorts, landscape video to in-stream, and the marketing
// images across Discover, the Display network and Gmail.
//
// 16:9 keeps in-stream first because it's the only Google placement
// that is natively landscape; everything else letterboxes it.
const OPTIONS: Record<ChromeFamily, Record<AdRatio, ChromeKind[]>> = {
  meta: {
    '9:16':   ['reels', 'stories', 'none'],
    '4:5':    ['ig-feed', 'fb-feed', 'none'],
    '1:1':    ['ig-feed', 'fb-feed', 'none'],
    '1.91:1': ['fb-feed', 'none'],
    '16:9':   ['fb-feed', 'none']
  },
  google: {
    '9:16':   ['yt-shorts', 'yt-infeed', 'discover', 'none'],
    '4:5':    ['discover', 'gdn-display', 'gmail', 'none'],
    '1:1':    ['discover', 'gdn-display', 'gmail', 'yt-infeed', 'none'],
    '1.91:1': ['gdn-display', 'discover', 'gmail', 'none'],
    // 16:9 is the one Google shape that exists on real Ad rows today
    // (the frozen pmax_16_9 key), and its own creative brief names
    // YouTube, Display, Discovery, Gmail and Maps — so it gets the full
    // fan-out rather than the video-only subset.
    '16:9':   ['yt-instream', 'yt-infeed', 'gdn-display', 'discover', 'gmail', 'none']
  }
};

const RATIOS: AdRatio[] = ['9:16', '4:5', '1:1', '1.91:1', '16:9'];

// Scope 'any' — every placement that runs at that shape, on any
// platform. Derived from the two tables rather than written out, so a
// placement added above can never be missing here. Meta leads, which
// keeps the pre-Google defaults (9:16 → Reels, feed shapes → IG Feed)
// exactly as they were for the one surface that asks for this.
const ANY_FAMILY: Record<AdRatio, ChromeKind[]> = RATIOS.reduce((acc, ratio) => {
  const merged = [...OPTIONS.meta[ratio], ...OPTIONS.google[ratio]].filter(k => k !== 'none');
  acc[ratio] = [...new Set(merged), 'none'];
  return acc;
}, {} as Record<AdRatio, ChromeKind[]>);

// An UNKNOWN platform is not the same as "show me everything". A legacy
// ad row with no platformFormat, or a format key from a build newer than
// this one, is overwhelmingly Meta — that is the entire live inventory —
// and offering it YouTube furniture would be exactly the defect this
// change exists to fix, pointed the other way. So unknown resolves to
// Meta, which is also what the Meta-only build did. Seeing every
// platform's placements requires asking for it: scope 'any'.
export const DEFAULT_SCOPE: ChromeScope = 'meta';

function optionsFor(ratio: AdRatio, scope: ChromeScope = DEFAULT_SCOPE): ChromeKind[] {
  const table = scope === 'any' ? ANY_FAMILY : OPTIONS[scope];
  return table?.[ratio] || ['none'];
}

// platformFormat (+ an optional explicit override) → the scope to use.
export function resolveScope(platformFormat?: string | null, override?: ChromeScope | null): ChromeScope {
  return override ?? familyForPlatformFormat(platformFormat) ?? DEFAULT_SCOPE;
}

export function chromeOptionsForRatio(ratio: AdRatio, scope?: ChromeScope): ChromeOption[] {
  return optionsFor(ratio, scope).map(kind => ({ kind, label: CHROME_LABEL[kind] }));
}

export function isChromeValidForRatio(kind: ChromeKind, ratio: AdRatio, scope?: ChromeScope): boolean {
  return optionsFor(ratio, scope).includes(kind);
}

// Shape default — the most common placement for that shape on that
// platform.
export function defaultChromeForRatio(ratio: AdRatio, scope?: ChromeScope): ChromeKind {
  return optionsFor(ratio, scope)[0];
}

// Ad.platformFormat → the chrome that placement actually renders in.
// Keys mirror services/platformFormats.js; the surface prefix is what
// matters, so unknown slugs still resolve via the family default rather
// than falling back to the other platform's furniture.
export function chromeForPlatformFormat(platformFormat: string | null | undefined): ChromeKind | null {
  const pf = String(platformFormat || '');
  if (!pf) return null;

  // ── Meta ──
  if (pf.startsWith('meta_stories')) return 'stories';
  if (pf.startsWith('meta_reels'))   return 'reels';
  if (pf.startsWith('meta_feed'))    return 'ig-feed';

  // ── Google ──
  // Video keys are explicit about being video, so they can safely claim
  // the video-only placements.
  if (pf.startsWith('pmax_video_9_16'))  return 'yt-shorts';
  if (pf.startsWith('pmax_video_16_9'))  return 'yt-instream';
  if (pf.startsWith('pmax_video_1_1'))   return 'discover';
  if (pf.startsWith('google_shorts'))    return 'yt-shorts';
  // Static marketing images: Discover for the feed shapes, Display for
  // the wide banner shape.
  if (pf.startsWith('pmax_landscape'))   return 'gdn-display';
  if (pf.startsWith('pmax_square'))      return 'discover';
  if (pf.startsWith('pmax_portrait'))    return 'discover';
  if (pf.startsWith('google_demandgen')) return 'discover';
  // pmax_16_9 is the FROZEN legacy key and is the one format that
  // carries BOTH kinds (platformFormats.js: kinds ['image','video']),
  // so the key alone can't tell us whether it's a video. Display takes
  // both, YouTube in-stream takes only video — so Display is the
  // default that is never wrong, and in-stream stays one click away in
  // the 16:9 option list.
  if (pf.startsWith('pmax_16_9'))        return 'gdn-display';

  return null;
}

// The chrome to show before the operator touches the selector.
export function resolveDefaultChrome(
  ratio: AdRatio,
  platformFormat?: string | null,
  scope?: ChromeScope
): ChromeKind {
  const s = scope ?? resolveScope(platformFormat);
  const declared = chromeForPlatformFormat(platformFormat);
  if (declared && isChromeValidForRatio(declared, ratio, s)) return declared;
  return defaultChromeForRatio(ratio, s);
}

// ── Persistence ──────────────────────────────────────────────────────
// Stored per family + ratio so switching between a 9:16 and a 4:5 ad
// doesn't force a re-pick — and so a Meta pick never leaks onto a Google
// ad, which is what a ratio-only key would do the moment both families
// offer placements at the same shape. Namespaced to survive alongside
// the rest of the app's localStorage usage (brand_id, advertiser_id, …).
//
// The key is deliberately NOT the old `ad_chrome_by_ratio`: entries
// written by the Meta-only build are keyed by bare ratio, which is now
// ambiguous. Rather than migrate a preview preference, we abandon it —
// worst case an operator re-picks once.

const STORAGE_KEY = 'ad_chrome_by_placement';

type StoredMap = Record<string, ChromeKind>;

function storageKeyFor(ratio: AdRatio, scope: ChromeScope = DEFAULT_SCOPE): string {
  return `${scope}|${ratio}`;
}

export function readStoredChrome(ratio: AdRatio, scope?: ChromeScope): ChromeKind | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const map = JSON.parse(raw) as StoredMap;
    const kind = map?.[storageKeyFor(ratio, scope)];
    return kind && isChromeValidForRatio(kind, ratio, scope) ? kind : null;
  } catch {
    return null;
  }
}

export function writeStoredChrome(ratio: AdRatio, kind: ChromeKind, scope?: ChromeScope): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const map: StoredMap = raw ? JSON.parse(raw) : {};
    map[storageKeyFor(ratio, scope)] = kind;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* storage disabled / quota — selection just doesn't persist */
  }
}
